export const AGENT_TYPES = ['country', 'city', 'super-agent', 'agent'];

/** Minimum wallet balance for a player to count as funded, per currency. */
export const FUNDED_THRESHOLDS = { USD: 10, LBP: 1000000, TND: 10, TRY: 15, IQD: 15000, KWD: 3 };

const DAY_MS = 24 * 60 * 60 * 1000;

export const balanceValue = (raw) => {
  if (raw && typeof raw === 'object') return parseFloat(raw.amount ?? raw.value) || 0;
  return parseFloat(raw) || 0;
};

export const defaultCurrency = (currencies) => {
  if (!currencies || currencies.length === 0) return 'USD';
  if (currencies.includes('LBP')) return 'LBP';
  if (currencies.includes('USD')) return 'USD';
  return currencies[0];
};

export const collectPlayerUsernames = (tree) => {
  const usernames = [];
  const walk = (node) => {
    if (!node) return;
    if (node.type === 'player') usernames.push(node.username);
    (node.children || []).forEach(walk);
  };
  walk(tree);
  return usernames;
};

/** Username -> { name, parent } for every player, where parent is the nearest agent-level ancestor's name. */
export const buildPlayerLookup = (tree) => {
  const lookup = new Map();
  const walk = (node, parentName) => {
    if (!node) return;
    if (node.type === 'player') lookup.set(node.username, { name: node.name || node.username, parent: parentName });
    const nextParent = AGENT_TYPES.includes(node.type) ? node.name || node.username : parentName;
    (node.children || []).forEach((child) => walk(child, nextParent));
  };
  walk(tree, null);
  return lookup;
};

/**
 * Snapshot of everyone below the viewer (the viewer's own wallet is reported separately):
 * balances per currency, balances per level, player and agent counts, and the balance leaderboards.
 */
export const summarizeNetwork = (tree, currency, now = Date.now()) => {
  const summary = {
    currencies: new Set(tree?.attributes?.currencies || []),
    ownBalance: balanceValue(tree?.balances?.[currency]),
    balances: {},
    byType: new Map(),
    players: { total: 0, blocked: 0, newThisWeek: 0, funded: 0 },
    agents: { total: 0, blocked: 0, withPlayers: 0, byType: { country: 0, city: 0, 'super-agent': 0, agent: 0 } },
    topPlayers: [],
    topAgents: []
  };
  const weekAgo = now - 7 * DAY_MS;
  const fundedThreshold = FUNDED_THRESHOLDS[currency] ?? 0;

  const walk = (node, parentName) => {
    const balance = balanceValue(node.balances?.[currency]);
    const blocked = node.attributes?.blocked === true;

    (node.attributes?.currencies || []).forEach((code) => {
      summary.currencies.add(code);
      summary.balances[code] = (summary.balances[code] || 0) + balanceValue(node.balances?.[code]);
    });

    if (!summary.byType.has(node.type)) summary.byType.set(node.type, { type: node.type, count: 0, funded: 0, total: 0 });
    const level = summary.byType.get(node.type);
    level.count += 1;
    level.total += balance;
    if (balance > 0) level.funded += 1;

    if (node.type === 'player') {
      summary.players.total += 1;
      if (blocked) summary.players.blocked += 1;
      const created = Date.parse(node.creationDate || node.attributes?.creationDate);
      if (Number.isFinite(created) && created >= weekAgo) summary.players.newThisWeek += 1;
      if (!blocked && balance > 0 && balance >= fundedThreshold) summary.players.funded += 1;
      if (balance > 0) summary.topPlayers.push({ username: node.username, name: node.name || node.username, balance, parent: parentName });
    } else if (AGENT_TYPES.includes(node.type)) {
      summary.agents.total += 1;
      summary.agents.byType[node.type] += 1;
      if (blocked) summary.agents.blocked += 1;
      const directPlayers = (node.children || []).filter((child) => child.type === 'player');
      if (directPlayers.length > 0) summary.agents.withPlayers += 1;
      summary.topAgents.push({
        username: node.username,
        name: node.name || node.username,
        type: node.type,
        playerCount: directPlayers.length,
        playerBalance: directPlayers.reduce((sum, player) => sum + balanceValue(player.balances?.[currency]), 0)
      });
    }

    const nextParent = AGENT_TYPES.includes(node.type) ? node.name || node.username : parentName;
    (node.children || []).forEach((child) => walk(child, nextParent));
  };

  (tree?.children || []).forEach((child) => walk(child, tree.name || tree.username));

  return {
    ...summary,
    currencies: Array.from(summary.currencies),
    byType: Array.from(summary.byType.values()).sort((a, b) => b.total - a.total),
    topPlayers: summary.topPlayers.sort((a, b) => b.balance - a.balance).slice(0, 10),
    topAgents: summary.topAgents
      .filter((agent) => agent.playerCount > 0)
      .sort((a, b) => b.playerBalance - a.playerBalance)
      .slice(0, 10)
  };
};
